import type { ParsedUrlQuery } from 'querystring';
import { css } from '@emotion/react';
import { Box, Flex, desktopFirstMediaQuery } from '@inflearn/ds-react-next';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { type MouseEvent, type ReactElement, useRef } from 'react';
import { getCdnTranslationProps } from '../../../utils/server/i18n/getCdnTranslationProps';

import AppShellLayoutForSSR from '../../../components/layout/AppShellLayoutForSSR';
import CustomHead from '../../../components/layout/CustomHead';
import { SubscriptionCoursesContactSection } from '../../../components/pages/subscription/courses/SubscriptionCoursesContactSection';
import { SubscriptionCoursesFilterBar } from '../../../components/pages/subscription/courses/SubscriptionCoursesFilterBar';
import { SubscriptionCoursesGrid } from '../../../components/pages/subscription/courses/SubscriptionCoursesGrid';
import { SubscriptionCoursesHero } from '../../../components/pages/subscription/courses/SubscriptionCoursesHero';
import { INFLEARN_URL } from '../../../constants/urls';
import useCheckLoginSession from '../../../hooks/useCheckLoginSession';
import { useCourseCardActions } from '../../../hooks/useCourseCardActions';
import { useLoginModalContext } from '../../../providers/ControlLoginModalContextProvider';
import {
  SUBSCRIPTION_COURSES_DEFAULT_FILTER,
  SUBSCRIPTION_COURSES_SORT,
  type SubscriptionCoursesFilter,
  type SubscriptionCoursesSort,
  getSubscriptionCoursesQueryKey,
  useSubscriptionCoursesQuery,
} from '../../../queries/useSubscriptionCoursesQuery';

const QUERY_KEYWORD = 'keyword';
const QUERY_CATEGORY = 'category';
const QUERY_SORT = 'sort';
const QUERY_PAGE = 'page';
const QUERY_PARTNER = 'partner';

/**
 * 인재개발플랫폼(mpm) 구매 페이지의 '구독 강의 전체 보기'가 붙여 보내는 값.
 * 그 방문자는 자기 플랫폼에서 구독권을 사는 개인이라 기업 도입을 권하는 접점
 * (히어로 CTA 둘·하단 문의 섹션)을 전부 감춘다.
 */
const PARTNER_MPM = 'mpm';

const SORT_VALUES = Object.values(SUBSCRIPTION_COURSES_SORT);

export const getStaticProps = async ({ locale }: { locale: string }) => ({
  props: {
    ...(await getCdnTranslationProps(locale, ['common'])),
  },
});

function parseKeyword(value: unknown) {
  return typeof value === 'string' ? value : '';
}

function parseCategorySlug(value: unknown) {
  return typeof value === 'string' && value !== '' ? value : null;
}

function parseSort(value: unknown, keyword: string): SubscriptionCoursesSort {
  const sort =
    SORT_VALUES.find((candidate) => candidate === value) ??
    SUBSCRIPTION_COURSES_DEFAULT_FILTER.sort;

  // 정확도는 키워드로 계산하는 점수라, 키워드 없이 들어온 URL 은 기본 정렬로 돌린다
  if (sort === SUBSCRIPTION_COURSES_SORT.SEARCH && keyword === '') {
    return SUBSCRIPTION_COURSES_DEFAULT_FILTER.sort;
  }

  return sort;
}

function parsePage(value: unknown) {
  const parsed = Number(parseKeyword(value));

  return Number.isInteger(parsed) && parsed > 0 ? parsed : 1;
}

function parsePartner(value: unknown) {
  return value === PARTNER_MPM ? PARTNER_MPM : null;
}

function parseFilter(query: ParsedUrlQuery): SubscriptionCoursesFilter {
  const keyword = parseKeyword(query[QUERY_KEYWORD]);

  return {
    keyword,
    categorySlug: parseCategorySlug(query[QUERY_CATEGORY]),
    sort: parseSort(query[QUERY_SORT], keyword),
    page: parsePage(query[QUERY_PAGE]),
  };
}

function SubscriptionCoursesPage() {
  const { t } = useTranslation();
  const router = useRouter();
  const listTopRef = useRef<HTMLDivElement>(null);

  const filter = parseFilter(router.query);
  const partner = parsePartner(router.query[QUERY_PARTNER]);
  const hideBusinessCtas = partner === PARTNER_MPM;

  const coursesQuery = useSubscriptionCoursesQuery(filter, { enabled: router.isReady });
  const { isLoggedIn } = useCheckLoginSession();
  const { open: openLoginModal } = useLoginModalContext();

  const { onClickLikeButton, onClickCartButton } = useCourseCardActions({
    invalidatedQueryKey: getSubscriptionCoursesQueryKey(filter),
    isAuthorized: isLoggedIn,
    onUnauthorized: openLoginModal,
  });

  const handleClickMoveToCart = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();

    void router.push(INFLEARN_URL.CARTS());
  };

  const pushFilter = (next: SubscriptionCoursesFilter) => {
    const query: Record<string, string> = {};

    if (next.keyword) {
      query[QUERY_KEYWORD] = next.keyword;
    }

    if (next.categorySlug) {
      query[QUERY_CATEGORY] = next.categorySlug;
    }

    if (next.sort !== SUBSCRIPTION_COURSES_DEFAULT_FILTER.sort) {
      query[QUERY_SORT] = next.sort;
    }

    if (next.page > 1) {
      query[QUERY_PAGE] = String(next.page);
    }

    // 필터는 쿼리를 새로 쓰므로 진입 경로 표식을 따로 실어 줘야 페이지 안에서 유지된다
    if (partner) {
      query[QUERY_PARTNER] = partner;
    }

    void router.push({ query }, undefined, { shallow: true, scroll: false });
  };

  // 필터가 바뀌면 총 페이지 수가 달라지므로 페이지를 1로 되돌린다
  const handleSubmitKeyword = (keyword: string) =>
    pushFilter({
      ...filter,
      keyword,
      sort:
        keyword === ''
          ? SUBSCRIPTION_COURSES_DEFAULT_FILTER.sort
          : SUBSCRIPTION_COURSES_SORT.SEARCH,
      page: 1,
    });

  const handleChangeCategory = (categorySlug: string | null) =>
    pushFilter({ ...filter, categorySlug, page: 1 });

  const handleChangeSort = (sort: SubscriptionCoursesSort) =>
    pushFilter({ ...filter, sort, page: 1 });

  const handleResetFilter = () =>
    pushFilter({
      ...filter,
      keyword: '',
      categorySlug: null,
      sort: SUBSCRIPTION_COURSES_DEFAULT_FILTER.sort,
      page: 1,
    });

  const handleChangePage = (page: number) => {
    pushFilter({ ...filter, page });
    listTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <Box component="article" w="100%">
      <CustomHead
        title={t('subscription_courses.meta.title')}
        description={t('subscription_courses.meta.description')}
      />
      <SubscriptionCoursesHero hideBusinessCtas={hideBusinessCtas} />
      <Box component="section" ref={listTopRef} css={styleListSection}>
        <Flex direction="column" gap={28} css={styleListStack}>
          <SubscriptionCoursesFilterBar
            keyword={filter.keyword}
            categorySlug={filter.categorySlug}
            sort={filter.sort}
            onSubmitKeyword={handleSubmitKeyword}
            onChangeCategory={handleChangeCategory}
            onChangeSort={handleChangeSort}
          />
          <SubscriptionCoursesGrid
            items={coursesQuery.items}
            totalCount={coursesQuery.totalCount}
            isLoading={coursesQuery.isLoading}
            isError={coursesQuery.isError}
            page={filter.page}
            totalPage={coursesQuery.totalPage}
            hasFilter={Boolean(filter.keyword || filter.categorySlug)}
            onChangePage={handleChangePage}
            onResetFilter={handleResetFilter}
            onClickLikeButton={onClickLikeButton}
            onClickCartButton={onClickCartButton}
            onClickMoveToCartButton={handleClickMoveToCart}
          />
        </Flex>
      </Box>
      {hideBusinessCtas ? null : <SubscriptionCoursesContactSection />}
    </Box>
  );
}

SubscriptionCoursesPage.getLayout = (page: ReactElement) => (
  <AppShellLayoutForSSR showChanneltalk={false} footer={{ hasBottomNavigation: false }}>
    {page}
  </AppShellLayoutForSSR>
);

const styleListSection = css({
  margin: '0 auto',
  maxWidth: 1200,
  padding: '40px 32px',
  scrollMarginTop: 16,
  [desktopFirstMediaQuery.tablet]: {
    padding: '24px 16px',
  },
});

const styleListStack = css({
  minWidth: 0,
});

export default SubscriptionCoursesPage;
